import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  Inject,
  Input,
  DOCUMENT,
  ElementRef,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromProfilerStore from 'src/app/profiler-store';

import { Subscription } from 'rxjs/internal/Subscription';

import { ProfilerLofiQueryResult } from 'src/app/profiler/model/profiler-lofi-report';
import { MapLayerStyleService } from 'src/app/shared/atlas-mapping/services/map-layer-style-service';
import * as fromCore from 'src/app/core/store';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AiPromptTemplateLibraryService } from 'src/app/core/services/ai-prompt-template-library.service';
import { AiAssistantDialogComponent } from '../../../ai-assistant/ai-assistant-dialog/ai-assistant-dialog.component';
import { DialogWidth } from 'src/app/shared/atlas-dialog/enums/dialog-width.enum';
import { convertStatsDataToMarkDown } from 'src/app/shared/helpers/lofi-reports-helper';
import { isAiAssistantDialogOpen } from 'src/app/shared/utils/dialog-utils';
import { MatDialog } from '@angular/material/dialog';
@Component({
  selector: 'atlas-profiler-reports-panel',
  templateUrl: './profiler-reports-panel.component.html',
  styleUrls: ['./profiler-reports-panel.component.less'],
  standalone: false
})
export class ProfilerReportsPanelComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private subscription = new Subscription();
  private resizeObserver?: ResizeObserver;

  @Input() pieGridView: [number, number] = [250, 620];
  @Input() numberCardView: [number, number] = [300, 620];
  @Input() graphHeight: number;
  @ViewChild('panelRoot') panelRoot: ElementRef<HTMLDivElement>;

  identifier = this.getGUID();

  selectedQueryItem: ProfilerLofiQueryResult | null =
    new ProfilerLofiQueryResult();

  queryTotal: number = -1;
  queryItems: ProfilerLofiQueryResult[] = [];

  graphConfig: any = {};
  title: string = '';
  resizeTick = 0;

  isAiAssistantEnabled$ = this.profilerStore$.select(
    fromCore.isAiAssistantEnabled
  );

  profileName = '';

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private profilerStore$: Store<fromProfilerStore.State>,
    private layerStyleService: MapLayerStyleService,
    private dialogService: DialogService,
    private aiPromptService: AiPromptTemplateLibraryService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.profilerStore$
        .select(fromProfilerStore.getLofiReport)
        .subscribe((lofFiReport) => {
          if (lofFiReport) {
            this.queryItems = lofFiReport as ProfilerLofiQueryResult[];
            this.queryTotal = this.queryItems.length;
            let selectedQuery = this.queryItems[0];
            this.selectedQueryItem = selectedQuery;
            this.setGraphConfig(selectedQuery);
            this.updateTitle();
          } else {
            this.queryTotal = 0;
            this.queryItems = [];
            this.selectedQueryItem = null;
            this.title = '';
          }
        })
    );

    this.subscription.add(
      this.profilerStore$
        .select(fromProfilerStore.getProfilerState)
        .subscribe((profile) => {
          if (profile) {
            this.profileName = profile.name || '';
          }
        })
    );
  }

  ngAfterViewInit(): void {
    const root = this.panelRoot?.nativeElement;
    if (!root || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.resizeObserver = new ResizeObserver(() => {
      this.resizeTick++;
      requestAnimationFrame(() => {
        this._document.defaultView?.dispatchEvent(new Event('resize'));
      });
    });

    this.resizeObserver.observe(root);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.subscription.unsubscribe();
  }

  selectQuery(position: number) {
    this.activateDot(position);
    // Select the first query item as a fallback
    this.selectedQueryItem = this.queryItems[position];
    this.updateTitle();
    this.setGraphConfig(this.queryItems[position]);
  }

  onGraphClicked(evt: any) {
    if (evt.mapLayerId) {
      this.layerStyleService.toggleSpotlight(evt.mapLayerId, evt.name);
    }
  }

  onAiAssistantClicked(): void {
    // build prompy from stats

    var prompt =
      'Write a clear, concise paragraph explaining the following data. Avoid jargon and be suitable for a non-technical audience. \n';

    prompt += `Profile Name: **${this.profileName}**\n\n`;

    prompt = prompt + convertStatsDataToMarkDown(this.selectedQueryItem);

    // add prompt to the service
    this.aiPromptService.setCurrentPromptText(prompt);

    if (!isAiAssistantDialogOpen(this.dialog)) {
      this.dialogService.show(AiAssistantDialogComponent, {
        width: DialogWidth.Large,
        panelClass: 'dialog-full-width-height',
        disableClose: true,
        hasBackdrop: false
      });
    }
  }

  private activateDot(postion: number) {
    const container = this._document.getElementById(this.identifier);

    var dots = container?.querySelectorAll('.dot');

    if (dots) {
      dots.forEach((dot) => {
        dot.className = dot.className.replace(' active', '');
      });

      dots[postion].className += ' active';
    }
  }

  private setGraphConfig(selectedQuery: ProfilerLofiQueryResult) {
    this.graphConfig =
      selectedQuery === undefined ||
      selectedQuery.graphConfiguration == null ||
      selectedQuery.graphConfiguration == undefined
        ? {}
        : JSON.parse(selectedQuery.graphConfiguration);
  }

  private getGUID(): string {
    let d = new Date().getTime();
    const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (c) => {
        const r = ((d + Math.random() * 16) % 16) | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
    return guid;
  }

  private updateTitle(): void {
    if (this.selectedQueryItem) {
      this.title = this.selectedQueryItem.pageTitle;
    }
  }
}
